import React from 'react';
import { Modal, Form, Input, Typography } from 'antd';
import { BankAccountFormItem, VFileUpload, modalMethod } from 'components';
import { useGetFinanceBankCodes, useSavePartnerContractsBankAccount, type TBankAccount } from 'hooks';
import { FlexBoxCenter } from 'styles';

import { ModalContent } from '../styles';

interface AccountBankModalProps {
  open: boolean;
  partnerContractIdx?: number;
  bankAccount?: TBankAccount;
  onCancel?: () => void;
}

const AccountBankModal = ({ open, partnerContractIdx, bankAccount, onCancel }: AccountBankModalProps) => {
  const [form] = Form.useForm();
  const { data: bankCodes } = useGetFinanceBankCodes(); // 은행 코드 조
  const { mutateAsync: savePartnerContractsBankAccount } = useSavePartnerContractsBankAccount();
  const [submittable, setSubmittable] = React.useState<boolean>(false);

  const isAccount = Form.useWatch([], form);
  const isBankAccountFile = form?.getFieldValue('bankAccountFile')?.[0];

  React.useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true && isBankAccountFile))
      .catch(() => setSubmittable(false && isBankAccountFile));
  }, [form, isAccount, isBankAccountFile]);

  React.useEffect(() => {
    if (form && bankAccount) {
      form.setFieldsValue({ ...bankAccount, bankAccountFile: null, bank: bankAccount });
    }
  }, [form, bankAccount]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = async (values) => {
    const confirmResult = await modalMethod.confirm(`입력하신 계좌로 변경요청하시겠습니까?`, {
      cancelText: '닫기',
      okText: '변경요청'
    });
    if (!confirmResult) return;
    const formData = new FormData();
    const bankAccountFile = form?.getFieldValue('bankAccountFile')?.[0];
    const params = {
      bankCode: values?.bank.bankCode,
      bankName: bankCodes?.find(({ bankCode }) => bankCode === values?.bank.bankCode)?.bankName,
      bankAccountNumber: values?.bank.bankAccount,
      bankAccountHolder: values.bankAccountHolder,
      bankAccountFile
    };
    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value);
    });
    await savePartnerContractsBankAccount({ partnerContractIdx, params: formData });
    modalMethod.info('입금 계좌 변경요청이 완료되었습니다.');
    handleCancel();
  };

  const financeBankCodes = bankCodes?.map(({ bankCode, bankName }) => ({ bankId: bankCode, bankName }));
  return (
    <Modal
      title="입금계좌 변경요청"
      open={open}
      onCancel={handleCancel}
      okText="변경요청"
      okButtonProps={{
        form: 'accountForm',
        htmlType: 'submit',
        disabled: !submittable,
        style: { borderRadius: '4px' }
      }}
      cancelText="닫기"
      cancelButtonProps={{ style: { borderRadius: '4px' } }}
    >
      <ModalContent>
        <Form autoComplete="off" layout="vertical" form={form} name="accountForm" onFinish={handleSubmit}>
          <Form.Item
            name="bankAccountHolder"
            label="예금주명(대표자 또는 사업자명의)"
            rules={[{ required: true, message: '필수 입력 항목입니다.' }]}
          >
            <Input placeholder="예금주명(대표자 또는 사업자명의) 입력" maxLength={10} />
          </Form.Item>
          <BankAccountFormItem
            showSearch
            nonCheckAccountHolder
            isSearching={false}
            bankCodes={financeBankCodes || []}
            onSearch={undefined}
          />
          <Form.Item name="bankAccountFile" style={{ display: 'none' }} /> {/* form 자동 업데이트를 위한 필드 */}
          <Form.Item label="통장사본" shouldUpdate rules={[{ required: true }]}>
            {() => (
              <FlexBoxCenter align="baseline" gap="8px">
                <VFileUpload
                  maxCount={1}
                  accept="image/*"
                  value={form?.getFieldValue('bankAccountFile')}
                  onChange={(file) => form?.setFieldValue('bankAccountFile', file)}
                />
                <Typography.Text type="danger">사진은 40mb까지 첨부하실 수 있습니다.</Typography.Text>
              </FlexBoxCenter>
            )}
          </Form.Item>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default AccountBankModal;
